import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, X, FileText, Loader2, FileSpreadsheet, Download } from "lucide-react";

/**
 * PoImportButton — reusable button + modal for importing line items from a PO.
 * Accepts BOTH .pdf (AI-parsed via Gemini) and .xlsx (HDB fixed template — faster, no LLM).
 * Only the parsed line items are handed back via onItemsExtracted().
 */
export default function PoImportButton({
    invoiceType = "B2B",
    onItemsExtracted,
    label = "Import items from PO",
    testId = "po-import",
    variant = "ghost",
}) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const btnCls = {
        primary: "hdb-btn hdb-btn-primary",
        ghost: "hdb-btn hdb-btn-ghost",
        mint: "hdb-btn hdb-btn-mint",
    }[variant] || "hdb-btn hdb-btn-ghost";

    const isExcel = (f) => !!f && /\.(xlsx|xlsm)$/i.test(f.name);
    const isPdf = (f) => !!f && /\.pdf$/i.test(f.name);

    const parse = async () => {
        if (!file) return toast.error("Choose a file first");
        if (!isExcel(file) && !isPdf(file)) return toast.error("Upload a .pdf or .xlsx file");
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("invoice_type", invoiceType);
        try {
            const endpoint = isExcel(file) ? "/invoices/from-po-excel" : "/invoices/from-po";
            const r = await api.post(endpoint, fd, { timeout: 240_000 });
            const items = r.data.line_items || [];
            const stats = r.data.stats || { matched: 0, unmatched: 0, total: items.length };
            onItemsExtracted?.(items, {
                ...stats,
                po_number: r.data.po_number || "",
                buyer: r.data.buyer,
                source: isExcel(file) ? "excel" : "pdf",
            });
            toast.success(`Added ${items.length} item${items.length !== 1 ? "s" : ""} from ${isExcel(file) ? "Excel" : "PDF"} PO — ${stats.matched} matched, ${stats.unmatched} need pricing`);
            setOpen(false);
            setFile(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not parse the PO");
        }
        setBusy(false);
    };

    return (
        <>
            <button type="button" className={btnCls} onClick={() => setOpen(true)}
                    data-testid={`${testId}-btn`}>
                <Sparkles size={13} /> {label}
            </button>

            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
                     data-testid={`${testId}-modal`}>
                    <div className="hdb-card w-full max-w-lg p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="hdb-heading font-bold text-lg text-slate-900 flex items-center gap-2">
                                <FileText size={18} /> Import from Purchase Order
                            </h3>
                            <button className="text-slate-400 hover:text-slate-600" onClick={() => { setOpen(false); setFile(null); }}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                            <div className="flex items-start gap-2">
                                <FileSpreadsheet size={16} className="text-emerald-700 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-emerald-900">Recommended: HDB Excel template</div>
                                    <div className="text-[11px] text-emerald-800 mt-0.5">
                                        Skips AI text extraction — the structure is known.  Item matching against your Item Master still runs on both PDF and Excel (local first, AI only for ambiguous items).  Download, fill the yellow cells, upload back.
                                    </div>
                                </div>
                                <a href={`${API}/invoices/po-template.xlsx`}
                                   className="hdb-btn hdb-btn-ghost text-xs whitespace-nowrap"
                                   data-testid={`${testId}-download-template`}>
                                    <Download size={11} /> Download template
                                </a>
                            </div>
                        </div>

                        <p className="text-xs text-slate-500 mb-3">
                            Upload the filled Excel (preferred) or a PDF PO. Line items get appended; invoice type (<b className="text-slate-800">{invoiceType}</b>) stays as-is.
                        </p>

                        <label className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                            file ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 hover:border-slate-900 bg-slate-50/60"
                        }`}>
                            <Upload size={20} className="mx-auto text-slate-500 mb-2" />
                            <div className="text-sm text-slate-700 font-semibold">
                                {file ? file.name : "Drop or select the PO file (.xlsx preferred, .pdf accepted)"}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                Excel = fast, no AI. PDF = AI-parsed (native/typed PDFs only, not scans).
                            </div>
                            <input type="file"
                                   accept=".pdf,.xlsx,.xlsm,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                   className="hidden"
                                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                                   data-testid={`${testId}-file`} />
                        </label>

                        <div className="mt-5 flex justify-end gap-2">
                            <button className="hdb-btn hdb-btn-ghost" onClick={() => { setOpen(false); setFile(null); }} disabled={busy}>
                                Cancel
                            </button>
                            <button className="hdb-btn hdb-btn-primary" onClick={parse} disabled={!file || busy}
                                    data-testid={`${testId}-parse-btn`}>
                                {busy ? <><Loader2 size={13} className="animate-spin" /> Reading…</>
                                      : <><Sparkles size={13} /> Parse &amp; Append</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
